import { z } from 'zod'

import {
  EventType,
  ContributorRole,
  EventStatus,
  EventOrganizer,
  SessionStatus,
  SubEventType,
  SubEventStatus,
  SessionType,
  OptionType,
  BlankEnum,
} from '../../types'
import { preprocessId, preprocessBoolean } from '../../utils'
import type {
  Contributors,
  Events,
  SubEvents,
  Sessions,
  Options,
  SubEventsBase,
  WelcomeSubEvent,
  BreakSubEvent,
  PresentationSubEvent,
  WorkShopSubEvent,
  HotelSubEvent,
  MealSubEvent,
  MeetingSubEvent,
  ThesisPresentationSubEvent,
  OptionsBase,
  SessionOption,
  SubEventOption,
  CheckBoxOption,
  TextOption,
  NumberOption,
  User,
} from '../../types'

export interface ModifyEventSchemaType {
  id: number
  event: Events
  sessions: ({
    _showSubEvents?: boolean | null
  } & Omit<Sessions, 'event'>)[]
  subEvents: SubEvents[]
  options: Options[]
}
export interface ModifySubEventsSchemaType {
  sessions: Sessions[]
  subEvents: SubEvents[]
}
export interface ModifyOptionsSchemaType {
  options: Options[]
}

export const LogisticSchema = z.object({
  abstract_submission_start: z.string().datetime({ offset: true }).nullable().default(null),
  abstract_submission_end: z.string().datetime({ offset: true }).nullable().default(null),
  abstract_review_start: z.string().datetime({ offset: true }).nullable().default(null),
  abstract_review_end: z.string().datetime({ offset: true }).nullable().default(null),
  sponsorship_request_start: z.string().datetime({ offset: true }).nullable().default(null),
  sponsorship_request_end: z.string().datetime({ offset: true }).nullable().default(null),
})
export type LogisticSchema = z.infer<typeof LogisticSchema>

export const EventSchema = LogisticSchema.extend({
  id: preprocessId(),
  name: z.string().min(1),
  start: z.string().datetime({ offset: true }),
  end: z.string().datetime({ offset: true }),
  type: z.nativeEnum(EventType),
  location: z.number().nullable().default(null),
  description: z.string().default(''),
  status: z.nativeEnum(EventStatus).default(EventStatus.Draft),
  organizer: z.nativeEnum(EventOrganizer),
  picture: z.string().nullable().default(''),
  _new: preprocessBoolean().default(false),
}) satisfies z.ZodType<Events, z.ZodTypeDef, unknown>
export type EventSchema = z.infer<typeof EventSchema>

export const ContributorsSchema = z.object({
  roles: z.array(z.nativeEnum(ContributorRole)),
  user: z.object({
    id: z.number(),
    first_name: z.string(),
    last_name: z.string(),
  }) satisfies z.ZodType<User, z.ZodTypeDef, unknown>,
}) satisfies z.ZodType<Contributors, z.ZodTypeDef, unknown>
export type ContributorsSchema = z.infer<typeof ContributorsSchema>

export const SessionSchema = z.object({
  id: preprocessId(),
  name: z.string().min(1),
  short_name: z.string().default(''),
  color: z.string().default(''),
  start: z.string().datetime({ offset: true }),
  end: z.string().datetime({ offset: true }),
  type: z.nativeEnum(SessionType).or(z.nativeEnum(BlankEnum)).default(BlankEnum.Any),
  description: z.string().default(''),
  status: z.nativeEnum(SessionStatus).default(SessionStatus.Confirmed),
  comment: z.string().default(''),
  organizer: z.nativeEnum(EventOrganizer).or(z.nativeEnum(BlankEnum)).default(BlankEnum.Any),
  sponsor: z.string().default(''),
  tags: z.array(z.number()).default([]),
  event: z.coerce.number(),
  sub_events: z.array(z.number()).default([]),
  contributors: z.array(ContributorsSchema).default([]),
  location: z.number().nullish().default(null),
  rooms: z.array(z.number()).default([]),
  hide_from_generic_program: z.boolean().default(false),
  default_abstract_presentation_time: z.string().time().default('00:15:00'),
  default_abstract_qa_time: z.string().time().default('00:00:00'),
  _new: preprocessBoolean().default(false),
  _showSubEvents: z.boolean().default(false),
}) satisfies z.ZodType<Sessions, z.ZodTypeDef, unknown>
export type SessionSchema = z.infer<typeof SessionSchema>

export const SubEventBaseSchema = z.object({
  _new: preprocessBoolean().default(false),
  id: preprocessId(),
  sub_event_type: z.nativeEnum(SubEventType),
  name: z.string().min(1),
  start: z.string().datetime({ offset: true }),
  end: z.string().datetime({ offset: true }),
  event: z.coerce.number(),
  status: z.nativeEnum(SubEventStatus).default(SubEventStatus.Confirmed),
  description: z.string().default(''),
  sessions: z.array(z.number()).default([]),
  hide_from_generic_program: z.boolean().default(false),
  // --- According to sub_event_type ---
  rooms: z.array(z.number()).default([]),
  location: z.number().nullish().default(null),
  tags: z.array(z.number()).optional().default([]),
  contributors: z.array(ContributorsSchema).optional().default([]),
  comment: z.string().optional().default(''),
}) satisfies z.ZodType<SubEventsBase, z.ZodTypeDef, unknown>
export type SubEventBaseSchema = z.infer<typeof SubEventBaseSchema>

export const WelcomeSubEventSchema = SubEventBaseSchema.omit({
  location: true,
  rooms: true,
  tags: true,
  contributors: true,
  comment: true,
  sub_event_type: true,
}).merge(
  z.object({
    sub_event_type: z.literal(SubEventType.WelcomeSubEvent),
  })
) satisfies z.ZodType<WelcomeSubEvent, z.ZodTypeDef, unknown>
export type WelcomeSubEventSchema = z.infer<typeof WelcomeSubEventSchema>

export const BreakSubEventSchema = SubEventBaseSchema.omit({
  location: true,
  rooms: true,
  tags: true,
  contributors: true,
  comment: true,
  sub_event_type: true,
}).merge(
  z.object({
    sub_event_type: z.literal(SubEventType.BreakSubEvent),
  })
) satisfies z.ZodType<BreakSubEvent, z.ZodTypeDef, unknown>
export type BreakSubEventSchema = z.infer<typeof BreakSubEventSchema>

export const HotelSubEventSchema = SubEventBaseSchema.omit({
  location: true,
  rooms: true,
  tags: true,
  contributors: true,
  sub_event_type: true,
}).merge(
  z.object({
    sub_event_type: z.literal(SubEventType.HotelSubEvent),
  })
) satisfies z.ZodType<HotelSubEvent, z.ZodTypeDef, unknown>
export type HotelSubEventSchema = z.infer<typeof HotelSubEventSchema>

export const MealSubEventSchema = SubEventBaseSchema.omit({
  contributors: true,
  tags: true,
  sub_event_type: true,
}).merge(
  z.object({
    sub_event_type: z.literal(SubEventType.MealSubEvent),
  })
) satisfies z.ZodType<MealSubEvent, z.ZodTypeDef, unknown>
export type MealSubEventSchema = z.infer<typeof MealSubEventSchema>

export const PresentationSubEventSchema = SubEventBaseSchema.omit({
  sub_event_type: true,
}).merge(
  z.object({
    sub_event_type: z.literal(SubEventType.PresentationSubEvent),
  })
) satisfies z.ZodType<PresentationSubEvent, z.ZodTypeDef, unknown>
export type PresentationSubEventSchema = z.infer<typeof PresentationSubEventSchema>

export const WorkShopSubEventSchema = SubEventBaseSchema.omit({
  sub_event_type: true,
}).merge(
  z.object({
    sub_event_type: z.literal(SubEventType.WorkShopSubEvent),
  })
) satisfies z.ZodType<WorkShopSubEvent, z.ZodTypeDef, unknown>
export type WorkShopSubEventSchema = z.infer<typeof WorkShopSubEventSchema>

export const MeetingSubEventSchema = SubEventBaseSchema.omit({
  tags: true,
  sub_event_type: true,
}).merge(
  z.object({
    sub_event_type: z.literal(SubEventType.MeetingSubEvent),
  })
) satisfies z.ZodType<MeetingSubEvent, z.ZodTypeDef, unknown>
export type MeetingSubEventSchema = z.infer<typeof MeetingSubEventSchema>

export const ThesisPresentationSubEventSchema = SubEventBaseSchema.omit({
  sub_event_type: true,
}).merge(
  z.object({
    sub_event_type: z.literal(SubEventType.ThesisPresentationSubEvent),
  })
) satisfies z.ZodType<ThesisPresentationSubEvent, z.ZodTypeDef, unknown>
export type ThesisPresentationSubEventSchema = z.infer<typeof ThesisPresentationSubEventSchema>

export const SubEventSchema = z
  .object({
    sub_event_type: z.nativeEnum(SubEventType),
  })
  .passthrough()
  .pipe(
    z.discriminatedUnion('sub_event_type', [
      WelcomeSubEventSchema,
      BreakSubEventSchema,
      PresentationSubEventSchema,
      WorkShopSubEventSchema,
      HotelSubEventSchema,
      MealSubEventSchema,
      MeetingSubEventSchema,
      ThesisPresentationSubEventSchema,
    ]) satisfies z.ZodType<SubEvents, z.ZodTypeDef, unknown>
  )
export type SubEventSchema = z.infer<typeof SubEventSchema>

export const OptionsBaseSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.nativeEnum(OptionType),
  sub_event: z.number().nullable(),
  session: z.number().nullable(),
  registration_information: z.string(),
  deadline: z.string().nullable(),
  max_participants: z.number().nullable(),
  _new: preprocessBoolean().default(false),
}) satisfies z.ZodType<OptionsBase, z.ZodTypeDef, unknown>
export type OptionsBaseSchema = z.infer<typeof OptionsBaseSchema>

export const SessionOptionsSchema = OptionsBaseSchema.omit({
  type: true,
  sub_event: true,
}).merge(
  z.object({
    type: z.literal(OptionType.SessionOption),
  })
) satisfies z.ZodType<SessionOption, z.ZodTypeDef, unknown>
export type SessionOptionsSchema = z.infer<typeof SessionOptionsSchema>

export const SubEventOptionsSchema = OptionsBaseSchema.omit({
  type: true,
  session: true,
}).merge(
  z.object({
    type: z.literal(OptionType.SubEventOption),
  })
) satisfies z.ZodType<SubEventOption, z.ZodTypeDef, unknown>
export type SubEventOptionsSchema = z.infer<typeof SubEventOptionsSchema>

export const CheckboxOptionsSchema = OptionsBaseSchema.omit({
  type: true,
  session: true,
  sub_event: true,
}).merge(
  z.object({
    type: z.literal(OptionType.CheckBoxOption),
  })
) satisfies z.ZodType<CheckBoxOption, z.ZodTypeDef, unknown>
export type CheckboxOptionsSchema = z.infer<typeof CheckboxOptionsSchema>

export const TextOptionsSchema = OptionsBaseSchema.omit({
  type: true,
  session: true,
  sub_event: true,
  max_participants: true,
}).merge(
  z.object({
    type: z.literal(OptionType.TextOption),
  })
) satisfies z.ZodType<TextOption, z.ZodTypeDef, unknown>
export type TextOptionsSchema = z.infer<typeof TextOptionsSchema>

export const NumberOptionsSchema = OptionsBaseSchema.omit({
  type: true,
  session: true,
  sub_event: true,
  max_participants: true,
}).merge(
  z.object({
    type: z.literal(OptionType.NumberOption),
  })
) satisfies z.ZodType<NumberOption, z.ZodTypeDef, unknown>
export type NumberOptionsSchema = z.infer<typeof NumberOptionsSchema>

export const OptionsSchema = z
  .object({
    type: z.nativeEnum(OptionType),
  })
  .passthrough()
  .pipe(
    z.discriminatedUnion('type', [
      SessionOptionsSchema,
      SubEventOptionsSchema,
      CheckboxOptionsSchema,
      TextOptionsSchema,
      NumberOptionsSchema,
    ]) satisfies z.ZodType<Options, z.ZodTypeDef, unknown>
  )
export type OptionsSchema = z.infer<typeof OptionsSchema>

export const ModifyOptionsSchema = z.object({
  options: z.array(OptionsSchema),
}) satisfies z.ZodType<ModifyOptionsSchemaType, z.ZodTypeDef, unknown>
export type ModifyOptionsSchema = z.infer<typeof ModifyOptionsSchema>

export const ModifyEventWihtoutOptionsSchema = z.object({
  event: EventSchema,
  sessions: z.array(SessionSchema),
  subEvents: z.array(SubEventSchema),
  id: preprocessId(),
}) satisfies z.ZodType<Omit<ModifyEventSchemaType, 'options'>, z.ZodTypeDef, unknown>
export type ModifyEventWihtoutOptionsSchema = z.infer<typeof ModifyEventWihtoutOptionsSchema>

export const ModifyEventSchema = z.object({
  event: EventSchema,
  sessions: z.array(SessionSchema),
  subEvents: z.array(SubEventSchema),
  options: z.array(OptionsSchema),
  id: preprocessId(),
}) satisfies z.ZodType<ModifyEventSchemaType, z.ZodTypeDef, unknown>
export type ModifyEventSchema = z.infer<typeof ModifyEventSchema>

export const ModifySubEventsSchema = z.object({
  sessions: z.array(SessionSchema),
  subEvents: z.array(SubEventSchema),
}) satisfies z.ZodType<ModifySubEventsSchemaType, z.ZodTypeDef, unknown>
export type ModifySubEventsSchema = z.infer<typeof ModifySubEventsSchema>
